/* Shared motion helpers built on anime.js (v3). Spring easings everywhere; respects reduced motion. */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasAnime = typeof window.anime === 'function';
  const SPRING = 'spring(1, 300, 18, 8)';       // drawers, panels: fast, with a visible overshoot
  const SPRING_OUT = 'spring(1, 460, 32, 8)';   // drawers closing: quick, settles softly
  const SPRING_SOFT = 'spring(1, 90, 14, 0)';   // stripes
  const SPRING_SNAPPY = 'spring(1, 320, 26, 0)';// small elements

  /** Resolve when the animation finishes, or after a timeout (anime pauses in hidden tabs) — then force the end state. */
  const settle = (anim, el, endTransform, ms = 1200) =>
    Promise.race([anim.finished, new Promise((r) => setTimeout(r, ms))]).then(() => {
      if (anim.progress < 100) { anim.pause(); if (endTransform !== undefined) el.style.transform = endTransform; }
    });

  const motion = {
    reduced,
    SPRING, SPRING_OUT, SPRING_SOFT, SPRING_SNAPPY,
    /** Slide a drawer in from a side ('left' | 'right'). */
    drawerIn(el, side = 'left') {
      if (reduced || !hasAnime) { el.style.transform = 'translateX(0)'; return Promise.resolve(); }
      anime.remove(el);
      return settle(anime({ targets: el, translateX: [side === 'left' ? '-100%' : '100%', '0%'], easing: SPRING }), el, 'translateX(0%)');
    },
    drawerOut(el, side = 'left') {
      if (reduced || !hasAnime) { el.style.transform = ''; return Promise.resolve(); }
      anime.remove(el);
      return settle(anime({ targets: el, translateX: side === 'left' ? '-100%' : '100%', easing: 'easeInCubic', duration: 140 }), el, side === 'left' ? 'translateX(-100%)' : 'translateX(100%)', 600);
    },
    /**
     * Drawer choreography.
     * open: page behind eases back and away, panel springs in, items cascade in from the panel's edge,
     *       the bottom action rises last.
     * close: items fade at once, panel leaves quickly, page returns.
     */
    openDrawer(dialog, { side = 'left', items = [], footer = null } = {}) {
      const page = document.getElementById('MainContent');
      const dir = side === 'left' || side === 'top' ? -1 : 1;
      const axis = side === 'top' ? 'translateY' : 'translateX';
      if (reduced || !hasAnime) { dialog.style.transform = 'none'; if (!dialog.open) dialog.showModal(); return Promise.resolve(); }
      anime.remove([dialog, page, ...items, footer].filter(Boolean));
      // Hide the pieces *before* the dialog is shown so nothing flashes, then open and animate
      dialog.style.transform = `${axis}(${dir * 100}%)`;
      items.forEach((el) => { el.style.opacity = 0; });
      if (footer) footer.style.opacity = 0;
      if (!dialog.open) dialog.showModal();
      const tl = anime.timeline({ autoplay: true });
      tl.add({ targets: dialog, [axis]: [`${dir * 100}%`, '0%'], easing: SPRING }, 0);
      if (items.length) {
        tl.add({ targets: items, [axis]: [dir * -18, 0], opacity: [0, 1], easing: 'easeOutQuint', duration: 460, delay: anime.stagger(22, { start: 60 }) }, 0);
      }
      if (footer) tl.add({ targets: footer, translateY: [16, 0], opacity: [0, 1], easing: SPRING_SNAPPY }, 180);
      return settle(tl, dialog, `${axis}(0%)`, 1400).then(() => {
        items.forEach((el) => { el.style.opacity = ''; el.style.transform = ''; });
        if (footer) { footer.style.opacity = ''; footer.style.transform = ''; }
      });
    },
    closeDrawer(dialog, { side = 'left', items = [] } = {}) {
      const page = document.getElementById('MainContent');
      const dir = side === 'left' || side === 'top' ? -1 : 1;
      const axis = side === 'top' ? 'translateY' : 'translateX';
      if (reduced || !hasAnime) { dialog.style.transform = ''; if (page) page.style.transform = ''; return Promise.resolve(); }
      anime.remove([dialog, page, ...items].filter(Boolean));
      const tl = anime.timeline({ autoplay: true });
      if (items.length) tl.add({ targets: items, opacity: 0, [axis]: dir * -10, easing: 'easeInQuad', duration: 110, delay: anime.stagger(6, { direction: 'reverse' }) }, 0);
      tl.add({ targets: dialog, [axis]: `${dir * 100}%`, easing: SPRING_OUT }, 20);
      return settle(tl, dialog, `${axis}(${dir * 100}%)`, 1200).then(() => {
        if (page) page.style.transform = '';
        items.forEach((el) => { el.style.opacity = ''; el.style.transform = ''; });
      });
    },
    /** Expand an element from 0 to its natural height. */
    expand(el, { spring = SPRING_SNAPPY } = {}) {
      if (reduced || !hasAnime) return Promise.resolve();
      anime.remove(el);
      const h = el.offsetHeight;
      el.style.overflow = 'hidden';
      return settle(anime({ targets: el, height: [0, h], opacity: [0, 1], easing: spring }), el).then(() => {
        el.style.height = ''; el.style.overflow = ''; el.style.opacity = '';
      });
    },
    collapse(el) {
      if (reduced || !hasAnime) return Promise.resolve();
      anime.remove(el);
      el.style.overflow = 'hidden';
      return settle(anime({ targets: el, height: 0, opacity: 0, easing: 'easeInQuad', duration: 240 }), el, undefined, 600).then(() => {
        el.style.height = ''; el.style.overflow = ''; el.style.opacity = '';
      });
    },
    /**
     * Unfold / fold an inline panel that pushes what's below it. The stylesheet owns the two rest states — folded
     * (no class) and `.is-open` — and this springs the height between them from wherever the element is *now*, so a
     * call mid-flight simply turns it around. `items` cascade in one after another as the space opens, and go out
     * from the bottom up as it folds. Resolves once at rest; a superseded call resolves without touching anything.
     */
    unfold(el, open, { items = [], spring = SPRING, springOut = SPRING_OUT } = {}) {
      const token = (el._unfold = (el._unfold || 0) + 1);
      const rest = () => {
        el.style.height = ''; el.style.visibility = '';
        items.forEach((item) => { item.style.opacity = ''; item.style.transform = ''; });
      };
      if (reduced || !hasAnime) { el.classList.toggle('is-open', open); rest(); return Promise.resolve(); }
      anime.remove([el, ...items]);
      const from = el.offsetHeight; // where it is now, before the class changes the rest height underneath
      el.classList.toggle('is-open', open);
      el.style.height = ''; // the target rest height, read in the same frame so nothing paints
      const to = el.offsetHeight;
      el.style.height = `${from}px`;
      el.style.visibility = 'visible';
      const step = Math.min(open ? 30 : 14, (open ? 480 : 200) / Math.max(items.length, 1)); // the cascade spans ~½s at most
      const tl = anime.timeline({ autoplay: true });
      if (open) {
        // items at rest were folded away unseen; ones mid-flight carry on from where they are
        items.forEach((item) => { if (item.style.opacity === '') { item.style.opacity = 0; item.style.transform = 'translateY(6px)'; } });
        tl.add({ targets: el, height: to, easing: spring }, 0);
        if (items.length) tl.add({ targets: items, opacity: 1, translateY: 0, easing: 'easeOutQuint', duration: 420, delay: anime.stagger(step, { start: 40 }) }, 0);
      } else {
        if (items.length) tl.add({ targets: items, opacity: 0, easing: 'easeOutQuad', duration: 140, delay: anime.stagger(step, { direction: 'reverse' }) }, 0);
        tl.add({ targets: el, height: to, easing: springOut }, 60);
      }
      return settle(tl, el, undefined, 1600).then(() => { if (el._unfold === token) rest(); });
    },
    /**
     * One list item gives way to another in its place (a cart line and its "removed" note, either way round): the first
     * fades and folds shut while the second unfolds. The first is taken out of the DOM but left clean, so it can come back.
     */
    swapItem(el, next) {
      el.after(next);
      const reset = (n) => { ['height', 'opacity', 'overflow', 'marginTop', 'pointerEvents'].forEach((prop) => { n.style[prop] = ''; }); };
      if (reduced || !hasAnime) { el.remove(); return Promise.resolve(); }
      anime.remove([el, next]);
      const h = next.offsetHeight; // natural height, read before it is folded
      const gap = parseFloat(getComputedStyle(el.parentElement).rowGap) || 0;
      el.style.pointerEvents = 'none';
      el.style.overflow = 'hidden';
      next.style.marginTop = `${-gap}px`; // the list's gap between the two is hidden: the pair reads as one slot changing height
      next.style.overflow = 'hidden';
      next.style.height = '0px';
      next.style.opacity = 0;
      const tl = anime.timeline({ autoplay: true });
      tl.add({ targets: el, opacity: 0, easing: 'easeOutQuad', duration: 140 }, 0);
      tl.add({ targets: el, height: 0, easing: 'easeInOutQuad', duration: 260 }, 80);
      tl.add({ targets: next, height: h, opacity: 1, easing: 'easeInOutQuad', duration: 260 }, 80);
      return settle(tl, el, undefined, 800).then(() => { el.remove(); reset(el); reset(next); });
    },
    /** Stagger a list of elements in (used after cart / grid re-renders). */
    riseIn(targets, { stagger = 40 } = {}) {
      if (reduced || !hasAnime) return Promise.resolve();
      return anime({ targets, translateY: [6, 0], opacity: [0, 1], easing: SPRING_SNAPPY, delay: anime.stagger(stagger, { start: 40 }) }).finished;
    },
    /** Quick press feedback. */
    pulse(el) {
      if (reduced || !hasAnime) return;
      anime.remove(el);
      anime({ targets: el, scale: [1, 0.97, 1], duration: 360, easing: 'easeOutElastic(1, 0.6)' });
    },
  };
  window.CJR = window.CJR || {};
  window.CJR.motion = motion;

  /* Reveal on scroll: [data-reveal] elements fade and rise in as they enter the viewport, staggered per batch.
     The step is short against the 700ms rise (critical.css) so half a dozen tiles are in motion at once: a wave, not a sequence */
  const REVEAL_STEP = 45;
  const revealables = document.querySelectorAll('[data-reveal]');
  // Always set up (even with no tiles at load): the product page's recommendations arrive later and rely on the observers
  if ('IntersectionObserver' in window && !reduced) {
    const io = new IntersectionObserver((entries) => {
      let n = 0;
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.style.setProperty('--reveal-delay', `${Math.min(n, 11) * REVEAL_STEP}ms`);
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
        n += 1;
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });
    revealables.forEach((el) => io.observe(el));
    // safety: anything already in view is revealed right away (and again when a hidden tab becomes visible)
    const revealInView = () => {
      let n = 0;
      document.querySelectorAll('[data-reveal]:not(.is-in)').forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          el.style.setProperty('--reveal-delay', `${Math.min(n, 11) * REVEAL_STEP}ms`);
          el.classList.add('is-in');
          io.unobserve(el);
          n += 1;
        }
      });
    };
    revealInView();
    document.addEventListener('visibilitychange', () => { if (!document.hidden) revealInView(); });
    // elements added later (filter re-render) get observed too
    new MutationObserver(() => document.querySelectorAll('[data-reveal]:not(.is-in)').forEach((el) => io.observe(el)))
      .observe(document.body, { childList: true, subtree: true });
  } else {
    revealables.forEach((el) => el.classList.add('is-in'));
  }

  /* Hover italics run narrower than the upright, so a two-line title can snap to one line and shift the grid.
     [data-keep-lines]: read where the upright text breaks and pin those breaks with <br>, so the italic keeps the
     same lines. Re-measured once fonts are in, when the viewport width changes, and for tiles added by filtering. */
  const keepLines = () => {
    const els = [...document.querySelectorAll('[data-keep-lines]')];
    if (!els.length) return;
    // write: one span per break opportunity (words, and the halves of a hyphenated word), upright, so the natural breaks can be read
    els.forEach((el) => {
      const text = el.dataset.keepLinesText || (el.dataset.keepLinesText = el.textContent.trim().replace(/\s+/g, ' '));
      el.style.fontStyle = 'normal';
      el.replaceChildren(...text.split(' ').flatMap((word, i) => word.match(/[^-\u2010-\u2014]*[-\u2010-\u2014]?/g).filter(Boolean).flatMap((part, j) => {
        const s = document.createElement('span');
        s.textContent = part;
        s.dataset.sep = i && !j ? ' ' : ''; // 'SCOOP-BACK' → 'SCOOP-' + 'BACK', glued back without a space
        return i && !j ? [' ', s] : [s];
      })));
    });
    // read: group the pieces by the line they landed on
    const lines = els.map((el) => {
      const out = [];
      let top = null;
      el.querySelectorAll('span').forEach((s) => {
        const t = Math.round(s.getBoundingClientRect().top);
        if (t !== top) { out.push(''); top = t; } else out[out.length - 1] += s.dataset.sep;
        out[out.length - 1] += s.textContent;
      });
      return out;
    });
    // write: plain text again, with a hard break between the measured lines
    els.forEach((el, i) => {
      el.style.fontStyle = '';
      el.replaceChildren(...lines[i].flatMap((line, j) => (j ? [document.createElement('br'), line] : [line])));
    });
  };
  if (document.querySelector('[data-keep-lines]') || document.querySelector('[data-collection-grid]')) {
    document.fonts.ready.then(keepLines);
    document.fonts.addEventListener('loadingdone', keepLines); // a late font changes the measure
    let width = window.innerWidth;
    let resizeTimer;
    window.addEventListener('resize', () => {
      if (window.innerWidth === width) return; // height-only changes (mobile URL bar) don't move the breaks
      width = window.innerWidth;
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(keepLines, 120);
    });
    new MutationObserver(() => { if (document.querySelector('[data-keep-lines]:not([data-keep-lines-text])')) keepLines(); })
      .observe(document.body, { childList: true, subtree: true });
  }

  /* Hover / touch prefetch fallback for browsers without Speculation Rules (see layout/theme.liquid). */
  if (!(HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules'))) {
    const seen = new Set();
    const skip = /^\/(cart|checkout|account|customer_authentication|search|admin)/;
    const prefetch = (e) => {
      const a = e.target.closest('a[href]');
      if (!a || a.origin !== location.origin || skip.test(a.pathname) || a.pathname === location.pathname) return;
      const url = a.pathname + a.search;
      if (seen.has(url)) return;
      seen.add(url);
      const link = document.createElement('link');
      link.rel = 'prefetch'; link.href = url; link.as = 'document';
      document.head.appendChild(link);
    };
    document.addEventListener('mouseover', prefetch, { passive: true });
    document.addEventListener('touchstart', prefetch, { passive: true });
  }
})();
